import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Link } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { Match } from '../types/api';

interface DecodedToken {
    id: string;
    email: string;
    role: string;
}

const Matches = () => {
    const [matches, setMatches] = useState<Match[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentUserId, setCurrentUserId] = useState<string | null>(null);

    const parseScore = (score: string | undefined) => {
        try {
            return score ? JSON.parse(score) : {};
        } catch (e) {
            console.warn('Neplatné skóre u zápasu', e);
            return {};
        }
    };

    useEffect(() => {
        // Get current user ID from token
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode<DecodedToken>(token);
                setCurrentUserId(decoded.id);
            } catch (error) {
                console.error('Failed to decode token:', error);
            }
        }

        const fetchMatches = async () => {
            try {
                const response = await api.get('/matches/history');
                setMatches(response.data);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchMatches();
    }, []);

    const getResultColor = (match: Match) => {
        if (!currentUserId) return 'bg-white/80';

        // Find the current user's participation
        const myParticipation = match.participants.find(
            p => p.userId === currentUserId
        );

        if (!myParticipation) return 'bg-white/80';

        // Return color based on win/loss
        if (myParticipation.winner) {
            return 'bg-gradient-to-r from-green-50 to-green-100 border-green-300';
        } else {
            return 'bg-gradient-to-r from-red-50 to-red-100 border-red-300';
        }
    };

    const getResultBadge = (match: Match) => {
        if (!currentUserId) return null;

        const myParticipation = match.participants.find(
            p => p.userId === currentUserId
        );

        if (!myParticipation || match.status !== 'finished') return null;

        if (myParticipation.winner) {
            return (
                <div className="inline-block px-3 py-1 bg-green-500 text-white rounded-lg text-xs font-bold">
                    Výhra
                </div>
            );
        } else {
            return (
                <div className="inline-block px-3 py-1 bg-red-500 text-white rounded-lg text-xs font-bold">
                    Prohra
                </div>
            );
        }
    };

    const getStatusBadge = (status: Match['status']) => {
        switch (status) {
            case 'pending':
                return <div className="inline-block px-3 py-1 bg-amber-500 text-white rounded-lg text-xs font-bold">Čeká na přijetí</div>;
            case 'started':
            case 'active':
                return <div className="inline-block px-3 py-1 bg-blue-500 text-white rounded-lg text-xs font-bold">Probíhá</div>;
            default:
                return <div className="inline-block px-3 py-1 bg-gray-200 text-gray-800 rounded-lg text-xs font-bold">Konečný</div>;
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('cs-CZ', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 p-4 sm:p-6 lg:p-8">
            <div className="max-w-4xl mx-auto">
                <div className="text-center mb-8 sm:mb-12">
                    <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent mb-2">
                        Historie zápasů
                    </h2>
                    <p className="text-gray-600">Přehled všech vašich odehraných zápasů</p>
                </div>

                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
                    </div>
                ) : matches.length === 0 ? (
                    <div className="bg-white/60 backdrop-blur-sm p-8 sm:p-12 rounded-3xl text-center border border-white/50">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">Zatím žádné zápasy</h3>
                        <p className="text-gray-600 mb-6">Je čas vyzvat soupeře a začít psát historii!</p>
                            <Link
                                to="/join-session"
                                className="inline-block px-8 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-bold rounded-xl shadow-lg hover:shadow-lg transition-all duration-200"
                            >
                                Hrát zápas
                        </Link>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {matches.map((match, index) => {
                            const score = parseScore(match.score);
                            const leftScore = Object.values(score)[0] || 0;
                            const rightScore = Object.values(score)[1] || 0;

                            return (
                                <div
                                    key={match.id}
                                    className={`backdrop-blur-lg p-6 rounded-3xl shadow-card border hover:shadow-card-hover transition-all duration-200 ${getResultColor(match)}`}
                                >
                                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                                        <div className="text-sm text-gray-500 font-medium">
                                            {formatDate(match.startTime)}
                                        </div>

                                        <div className="flex items-center justify-center gap-6 sm:gap-12 flex-1">
                                            <div className="text-center flex-1">
                                                <div className="font-bold text-gray-900 text-lg sm:text-xl">
                                                    {match.participants.filter(p => p.side === 'left').map(p => p.user?.name || p.team?.name || 'Hráč').join(', ')}
                                                </div>
                                            </div>

                                            <div className="flex flex-col items-center">
                                                <div className="text-3xl sm:text-4xl font-black bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent px-4 py-2 bg-gray-50 rounded-xl">
                                                    {match.status === 'pending' ? '— : —' : `${leftScore} : ${rightScore}`}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-1 uppercase tracking-wider font-bold">
                                                    {match.status === 'finished' ? 'Konečný' : match.status === 'pending' ? 'Čeká na přijetí' : 'Probíhá'}
                                                </div>
                                            </div>

                                            <div className="text-center flex-1">
                                                <div className="font-bold text-gray-900 text-lg sm:text-xl">
                                                    {match.participants.filter(p => p.side === 'right').map(p => p.user?.name || p.team?.name || 'Hráč').join(', ')}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="sm:w-32 text-right flex flex-col gap-2 items-end">
                                            <div className="inline-block px-3 py-1 bg-gray-100 text-gray-600 rounded-lg text-xs font-bold">
                                                {match.mode === 'competitive' ? '🏆 Soutěžní' : 'Friendly'}
                                            </div>
                                            {getStatusBadge(match.status)}
                                            {getResultBadge(match)}
                                            <Link
                                                to={`/matches/${match.id}`}
                                                className="text-xs text-primary-600 hover:text-secondary-600 font-semibold underline"
                                            >
                                                Detail
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Matches;
